import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, Route, RouteProps } from 'react-router';
import { skipOnboarding } from '../../components/auth/helpers/auth.api';
import { AuthHelper, getLeadType } from '../utils';
import { getToken } from '../utils/token';

type IProps = Omit<RouteProps, 'render'>;

const AuthRoute: React.FC<IProps> = ({ component: Component, ...rest }) => {
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  const determineRedirectPath = useCallback(async () => {
    const isLoginPath = /^\/login\/?$/.test(window?.location?.pathname);
    const searchParams = new URLSearchParams(window?.location?.search || '');
    const leadParam = searchParams?.get('lead') || '';
    const typeParam = searchParams?.get('type') || '';
    const isEmailVerifier =
      (searchParams?.get('utm_source') || ' ') === 'email-verifier';
    const isEmailSpamChecker =
      (searchParams?.get('utm_source') || ' ') === 'email-spam-checker';
    const isGrowthHub =
      (searchParams?.get('utm_source') || ' ') === 'growth-hub';

    try {
      // MCP OAuth: if already logged in and source=mcp, go to success page
      if (isLoginPath && searchParams?.get('source') === 'mcp') {
        const mcpReturnUrl = searchParams?.get('returnUrl') || '';
        if (mcpReturnUrl) {
          const token = getToken();
          if (token) {
            sessionStorage.setItem('mcp_token', token);
            setRedirectPath(`/mcp-auth-success?returnUrl=${encodeURIComponent(mcpReturnUrl)}`);
            return;
          }
        }
      }

      if (leadParam) {
        await skipOnboarding();

        // ** Redirect to lead page if lead param is present **
        if (isLoginPath) {
          setRedirectPath(
            `/leads?start=1&take=25&${getLeadType(typeParam)}=${leadParam}`,
          );
        } else {
          setRedirectPath('/');
        }
        return;
      }
      if (isLoginPath && isEmailVerifier) {
        setRedirectPath('/email-verifier');
        return;
      }
      if (isLoginPath && isEmailSpamChecker) {
        setRedirectPath('/inbox-radar');
        return;
      }
      if (isLoginPath && isGrowthHub) {
        setRedirectPath('/growth-hub');
        return;
      }
    } catch (error) {
      setRedirectPath('/');
      return;
    }
    setRedirectPath('/');
  }, []);

  useEffect(() => {
    if (AuthHelper.isAuthenticated) {
      determineRedirectPath();
    }
  }, []);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!AuthHelper.isAuthenticated) {
          return <Component {...props} />;
        }

        return redirectPath ? <Redirect to={redirectPath} /> : null;
      }}
    />
  );
};

export default AuthRoute;
